import { Autocomplete, Box, Chip, Stack, TextField, Tooltip, Typography, createFilterOptions } from '@mui/material'
import { AutocompleteRenderGroupParams } from '@mui/material/Autocomplete'
import { HTMLAttributes, Key, ReactNode, memo, useMemo } from 'react'
import { ISpendBasedConversion } from '../../../../shared/services/spend-based-conversion/spend-based-conversion.model'
import { getCategoryDisplayName } from '../../../../shared/utils/category-utils'
import { conversionCode, createDisplayFormatSpendBasedConversion, sortByConversionCode } from '../../../../shared/utils/conversion-utils'
import { formatNominalActivityName } from '../../../../shared/utils/nominal-code-utils'
import { VirtualizedListbox, renderVirtualizedGroup } from '../../form-field-components/virtualized-listbox'
import { INominalOutcomeValue } from './nominal-mapping-outcome-picker'

/**
 * NominalOutcomeAutocomplete
 * -------------------------------------------------------------------------
 * The one control a Chart of Accounts row needs: a single searchable list with "Exclude from
 * emissions" and "Don't import" at the top and then every spend activity of the organisation's
 * default source,
 * grouped by category. Choosing Exclude reveals an optional reason beside it. Anything rarer
 * (another source, a group to place the code in a scope) is behind the row menu's "Advanced",
 * which opens the full NominalMappingOutcomePicker in a drawer.
 *
 * An untouched row shows "Choose an outcome" and nothing else: it never pre-selects a spend
 * factor, so untouched and decided rows cannot be confused. The closed field shows the bare
 * activity name; the open list keeps the full "Code X: activity" text so a code can be searched.
 *
 * Value in and out is INominalOutcomeValue, the shape the picker, the add/edit dialog and the
 * payload builder share. Rendered once per row, so it is memoised and takes stable callbacks keyed
 * by `id` rather than a fresh closure per row.
 */

/** One activity option, built once per page from the default source's activities. */
export interface INominalActivityOption {
  kind: 'activity'
  key: string
  // The full display text, listed in the open list and searched on.
  label: string
  // The bare activity name, shown in the closed field.
  shortLabel: string
  group: string
  conversion: ISpendBasedConversion
}

type ExcludeOption = { kind: 'exclude', key: 'exclude', label: string, shortLabel: string, group: string }
type PrivateOption = { kind: 'private', key: 'private', label: string, shortLabel: string, group: string }
type RetiredOption = { kind: 'retired', key: 'retired', label: string, shortLabel: string, group: string }
type OutcomeOption = INominalActivityOption | ExcludeOption | PrivateOption | RetiredOption

// Options with no group heading: Exclude, and the current value when it is not in the list.
const NO_GROUP = ''
const OTHER_CATEGORY = 'Other'

const EXCLUDE_OPTION: ExcludeOption = { kind: 'exclude', key: 'exclude', label: 'Exclude from emissions', shortLabel: 'Exclude from emissions', group: NO_GROUP }
// "Don't import": the code's lines never enter the platform (wages and other personal data).
const PRIVATE_OPTION: PrivateOption = { kind: 'private', key: 'private', label: "Don't import (wages, personal data)", shortLabel: "Don't import", group: NO_GROUP }
// A code stored under the retired "captured via activity data" outcome must still render as its
// current value, or the row reads as unmapped. It cannot be chosen.
const RETIRED_OPTION: RetiredOption = { kind: 'retired', key: 'retired', label: 'Captured via activity data (retired)', shortLabel: 'Captured via activity data (retired)', group: NO_GROUP }

const toActivityOption = (conversion: ISpendBasedConversion, group: string): INominalActivityOption => {
  const label = createDisplayFormatSpendBasedConversion(conversion).displayText
  return {
    kind: 'activity',
    key: conversion.id,
    label,
    shortLabel: formatNominalActivityName(conversion) || label,
    group,
    conversion,
  }
}

/**
 * The activity options for a source, grouped by category display name and sorted by code inside
 * each group. MUI's groupBy needs the options already ordered by group. Uncategorised activities
 * sit last under "Other".
 */
export const buildNominalActivityOptions = (conversions: ISpendBasedConversion[]): INominalActivityOption[] => {
  const byGroup = new Map<string, ISpendBasedConversion[]>()
  conversions.forEach(conversion => {
    const group = getCategoryDisplayName(conversion.category) || OTHER_CATEGORY
    if (!byGroup.has(group)) byGroup.set(group, [])
    byGroup.get(group)!.push(conversion)
  })
  const groups = Array.from(byGroup.keys()).sort((a, b) => {
    if (a === OTHER_CATEGORY) return 1
    if (b === OTHER_CATEGORY) return -1
    return a.localeCompare(b)
  })
  return groups.flatMap(group => sortByConversionCode(byGroup.get(group)).map(conversion => toActivityOption(conversion, group)))
}

// Typing a category name finds its activities as well as typing the activity or its code.
const filterOptions = createFilterOptions<OutcomeOption>({ stringify: option => `${option.label} ${option.group}` })

// Exclude and the out-of-list current value are listed without a heading above them.
const renderGroup = (params: AutocompleteRenderGroupParams): ReactNode =>
  params.group === NO_GROUP ? params.children : renderVirtualizedGroup(params)

/**
 * The option a value should show as. A spend value is matched by id first; failing that, by the
 * same code and then the same activity in the same source, because factor-year resolution moves
 * the id between years (the supplier form's matchLoadedConversionId is the precedent). A value
 * that is in none of the listed activities (another source, picked in the drawer) is shown as an
 * extra option of its own so the row never reads as empty when it is not.
 */
const resolveSelected = (
  value: INominalOutcomeValue | null,
  activities: INominalActivityOption[],
  currentConversion: ISpendBasedConversion | null | undefined,
): { selected: OutcomeOption | null, extra: OutcomeOption | null } => {
  if (!value) return { selected: null, extra: null }
  if (value.mappingType === 'exclude') return { selected: EXCLUDE_OPTION, extra: null }
  if (value.mappingType === 'private') return { selected: PRIVATE_OPTION, extra: null }
  if (value.mappingType === 'activity') return { selected: RETIRED_OPTION, extra: RETIRED_OPTION }
  if (!value.spendBasedConversionId) return { selected: null, extra: null }

  const byId = activities.find(option => option.conversion.id === value.spendBasedConversionId)
  if (byId) return { selected: byId, extra: null }

  const known = currentConversion && currentConversion.id === value.spendBasedConversionId ? currentConversion : null
  if (known) {
    const sourceId = known.conversionSourceId || value.conversionSourceId
    const sameSource = activities.filter(option => !sourceId || option.conversion.conversionSourceId === sourceId)
    const code = conversionCode(known)
    const byCode = code ? sameSource.find(option => conversionCode(option.conversion) === code) : undefined
    const byActivity = known.activity ? sameSource.find(option => option.conversion.activity === known.activity) : undefined
    const matched = byCode || byActivity
    if (matched) return { selected: matched, extra: null }
    const extra = toActivityOption(known, NO_GROUP)
    return { selected: extra, extra }
  }

  // Nothing to name it by: still show that an activity is set rather than an empty row.
  const unnamed: INominalActivityOption = {
    kind: 'activity',
    key: value.spendBasedConversionId,
    label: 'Spend activity (from another factor set)',
    shortLabel: 'Spend activity (from another factor set)',
    group: NO_GROUP,
    conversion: { id: value.spendBasedConversionId, conversionSourceId: value.conversionSourceId } as ISpendBasedConversion,
  }
  return { selected: unnamed, extra: unnamed }
}

export interface INominalOutcomeAutocompleteProps {
  // Identifies the row to the callbacks, so one stable callback serves every row.
  id: string
  value: INominalOutcomeValue | null
  onChange: (id: string, next: INominalOutcomeValue, conversion: ISpendBasedConversion | null) => void
  activities: INominalActivityOption[]
  // The activity behind value.spendBasedConversionId when it is known (a saved mapping's included
  // conversion, or the one picked in the drawer). Lets a value from another year or source show.
  currentConversion?: ISpendBasedConversion | null
  // Bulk "Set activity": activities only, no Exclude, no reason field.
  activitiesOnly?: boolean
  // Shown as a small chip when the organisation uses more than one spend source.
  sourceLabel?: string | null
  loading?: boolean
  disabled?: boolean
  // Names the row for screen readers, e.g. "code 7400".
  codeLabel?: string
  // Focus the field when it mounts: a saved row just put back into edit mode.
  autoFocus?: boolean
}

const NominalOutcomeAutocompleteComponent = ({
  id,
  value,
  onChange,
  activities,
  currentConversion,
  activitiesOnly = false,
  sourceLabel,
  loading = false,
  disabled = false,
  codeLabel,
  autoFocus = false,
}: INominalOutcomeAutocompleteProps) => {
  const { selected, extra } = useMemo(
    () => resolveSelected(value, activities, currentConversion),
    [value, activities, currentConversion],
  )

  const options = useMemo<OutcomeOption[]>(() => {
    const leading: OutcomeOption[] = []
    if (!activitiesOnly) leading.push(EXCLUDE_OPTION, PRIVATE_OPTION)
    if (extra && (!activitiesOnly || extra.kind === 'activity')) leading.push(extra)
    return [...leading, ...activities]
  }, [activities, activitiesOnly, extra])

  const changeHandler = (_event: unknown, option: OutcomeOption | null) => {
    if (!option || option.kind === 'retired') return
    if (option.kind === 'private') {
      onChange(id, {
        mappingType: 'private',
        spendBasedConversionId: null,
        conversionSourceId: null,
        excludeReason: null,
        groupId: null,
      }, null)
      return
    }
    if (option.kind === 'exclude') {
      onChange(id, {
        mappingType: 'exclude',
        spendBasedConversionId: null,
        conversionSourceId: null,
        excludeReason: value?.mappingType === 'exclude' ? value.excludeReason ?? null : null,
        groupId: value?.groupId ?? null,
      }, null)
      return
    }
    onChange(id, {
      mappingType: 'spend',
      spendBasedConversionId: option.conversion.id,
      conversionSourceId: option.conversion.conversionSourceId ?? null,
      excludeReason: null,
      groupId: value?.groupId ?? null,
    }, option.conversion)
  }

  const reasonChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
    onChange(id, {
      mappingType: 'exclude',
      spendBasedConversionId: null,
      conversionSourceId: null,
      excludeReason: event.target.value,
      groupId: value?.groupId ?? null,
    }, null)
  }

  const showReason = !activitiesOnly && value?.mappingType === 'exclude'
  const showSourceChip = !activitiesOnly && !!sourceLabel && value?.mappingType === 'spend'

  return (
    <Stack direction='row' alignItems='center' useFlexGap spacing={1} flexWrap='wrap' sx={{ width: '100%' }}>
      {/* disableClearable: an untouched row starts empty, but once a row is set the way back is
          the row's "Undo change" or a bulk "Clear mapping", never an accidental click on an X. */}
      <Autocomplete<OutcomeOption, false, true, false>
        size='small'
        disableClearable
        autoHighlight
        disabled={disabled}
        loading={loading}
        options={options}
        value={selected as OutcomeOption}
        onChange={changeHandler}
        groupBy={option => option.group}
        renderGroup={renderGroup}
        ListboxComponent={VirtualizedListbox}
        filterOptions={filterOptions}
        getOptionLabel={option => option.shortLabel}
        getOptionDisabled={option => option.kind === 'retired'}
        isOptionEqualToValue={(option, current) => option.key === current.key}
        renderOption={(props, option) => {
          const { key, ...optionProps } = props as HTMLAttributes<HTMLLIElement> & { key: Key }
          return (
            <Box component='li' key={key} {...optionProps} title={option.label}>
              <Typography variant='inherit' noWrap sx={{ minWidth: 0, flex: 1 }}>{option.label}</Typography>
            </Box>
          )
        }}
        slotProps={{
          // Activity names are long and the column is not, so the list opens wider than the field.
          popper: { placement: 'bottom-start', sx: { minWidth: { md: 440 } } },
        }}
        sx={{ flex: 1, minWidth: 220 }}
        renderInput={(params) => (
          <TextField
            {...params}
            autoFocus={autoFocus}
            placeholder={activitiesOnly ? 'Choose an activity' : 'Choose an outcome'}
            inputProps={{ ...params.inputProps, 'aria-label': `${activitiesOnly ? 'Spend activity' : 'Outcome'}${codeLabel ? ` for ${codeLabel}` : ''}` }}
          />
        )}
      />
      {showReason && (
        <TextField
          size='small'
          placeholder='Reason (optional)'
          value={value?.excludeReason || ''}
          onChange={reasonChangeHandler}
          disabled={disabled}
          inputProps={{ 'aria-label': 'Reason for excluding (optional)' }}
          sx={{ flex: 1, minWidth: 160, maxWidth: 260 }}
        />
      )}
      {showSourceChip && (
        <Tooltip title={sourceLabel}>
          <Chip size='small' variant='outlined' label={sourceLabel} sx={{ maxWidth: 160 }} />
        </Tooltip>
      )}
    </Stack>
  )
}

export const NominalOutcomeAutocomplete = memo(NominalOutcomeAutocompleteComponent)
